import React, {useEffect, useState} from 'react';
import {
  ISupplierCompanyProfile,
  ISupplierUserProfile,
  ITraderApplication,
} from 'constants/interfaces';
import {FdCompanyForm} from 'components/applications/FdCompanyForm';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {
  apiSubmitTraderFormFields,
  apiUpdateCompany,
  ISubmitTraderFormFieldInput,
} from 'constants/Api';
import {
  ITraderFieldLike,
  lshCollectCompanyDetailsFieldsFromTraderApplication,
  lshGetTraderFieldCurrentValue,
  lshGetTraderFieldLabel,
} from 'screens/Dashboard/Applications/ApplicationDetail/Journey/tradingFormJourneyUtils';

type CompanyEditableFormKey = 'tradingName' | 'vatNumber' | 'taxNumber';

function lshToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function lshFieldId(field?: ITraderFieldLike): string {
  return lshToText(field?.id).toLowerCase();
}

function lshFieldLabel(field?: ITraderFieldLike): string {
  return lshToText(lshGetTraderFieldLabel(field)).toLowerCase();
}

function lshFindTraderFieldForCompanyKey(
  traderApplication: ITraderApplication | undefined,
  key: CompanyEditableFormKey,
): ITraderFieldLike | undefined {
  const fields =
    lshCollectCompanyDetailsFieldsFromTraderApplication(traderApplication);

  return fields.find((candidate) => {
    const id = lshFieldId(candidate);
    const label = lshFieldLabel(candidate);

    if (key === 'tradingName') {
      return (
        (id.includes('trading_name') || label.includes('trading name')) &&
        !id.includes('registration') &&
        !label.includes('registration')
      );
    }

    if (key === 'vatNumber') {
      return (
        (id.includes('vat_number') ||
          id.includes('vat_no') ||
          label.includes('vat number') ||
          (id.includes('vat') && !id.includes('upload'))) &&
        !id.includes('certificate') &&
        !label.includes('certificate') &&
        !id.includes('tax_pin') &&
        !label.includes('tax pin')
      );
    }

    return (
      (id.includes('tax_number') ||
        id.includes('tax_no') ||
        label.includes('tax number')) &&
      !id.includes('pin') &&
      !label.includes('pin') &&
      !id.includes('certificate') &&
      !label.includes('certificate')
    );
  });
}

function lshBuildTraderFormSubmission(
  field: ITraderFieldLike,
  nextValue: unknown,
): ISubmitTraderFormFieldInput | null {
  const fieldId = lshToText(field?.id);
  const fieldTypeName = lshToText((field as any)?.__typename);
  if (!fieldId || !fieldTypeName) return null;

  if (fieldTypeName === 'TradingFormCheckBox') {
    return {
      id: fieldId,
      value: String(Boolean(nextValue)),
      fieldTypeName,
    };
  }

  if (fieldTypeName === 'TradingFormMultiChoiceOption') {
    const values = Array.isArray(nextValue)
      ? nextValue
      : lshToText(nextValue).startsWith('[')
      ? nextValue
      : [nextValue];
    const normalised = Array.isArray(values)
      ? values.map((item) => lshToText(item)).filter(Boolean)
      : values;

    return {
      id: fieldId,
      value: JSON.stringify(normalised),
      fieldTypeName,
    };
  }

  return {
    id: fieldId,
    value: lshToText(nextValue ?? lshGetTraderFieldCurrentValue(field)),
    fieldTypeName,
  };
}

const AddOrUpdateFdCompany = (props: {
  user: ISupplierUserProfile;
  company?: ISupplierCompanyProfile;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  const canWrite = true;
  const isAdding = !props.company;
  const company = props.company;
  const traderApplication = (company as any)?.__traderApplication as
    | ITraderApplication
    | undefined;

  useEffect(() => {
    // Signal edit started
    reduxSetSidePanelEditState('editing');
    return () => {
      // Reset when panel unmounts if not saved
      reduxSetSidePanelEditState('idle');
    };
  }, []);

  const handleSubmit = (companyData: Partial<ISupplierCompanyProfile>) => {
    if (!canWrite) return;

    const companyId = String(companyData.id || company?.id || '').trim();
    if (!companyId) {
      alert('Failed to update company: missing company ID');
      return;
    }

    const bankId = String(
      companyData.bankId ||
        companyData.bank?.id ||
        company?.bankId ||
        company?.bank?.id ||
        '',
    ).trim();
    const bankAccountTypeId = String(
      companyData.bankAccountTypeId ||
        companyData.bankAccountType?.id ||
        company?.bankAccountTypeId ||
        company?.bankAccountType?.id ||
        '',
    ).trim();
    const normalise = (value?: string | number | null) =>
      String(value ?? '').trim();
    const hasChanged = (
      nextValue?: string | number | null,
      previousValue?: string | number | null,
    ) => normalise(nextValue) !== normalise(previousValue);

    const traderFieldCandidates: Array<{
      key: CompanyEditableFormKey;
      label: string;
      value: string;
    }> = [
      {
        key: 'tradingName',
        label: 'Trading name',
        value: lshToText(companyData.tradingName),
      },
      {
        key: 'vatNumber',
        label: 'VAT number',
        value: lshToText(companyData.vatNumber),
      },
      {
        key: 'taxNumber',
        label: 'Tax number',
        value: lshToText(companyData.taxNumber),
      },
    ];

    const traderFieldChanges = traderFieldCandidates.filter((item) =>
      hasChanged(item.value, lshToText((company as any)?.[item.key])),
    );

    const supportedFieldsChanged =
      hasChanged(companyData.name, company?.name) ||
      hasChanged(companyData.addressLine1, company?.addressLine1) ||
      hasChanged(companyData.addressLine2, company?.addressLine2) ||
      hasChanged(companyData.city, company?.city) ||
      hasChanged(companyData.province, company?.province) ||
      hasChanged(companyData.areaCode, company?.areaCode) ||
      hasChanged(bankId, company?.bankId || company?.bank?.id) ||
      hasChanged(
        bankAccountTypeId,
        company?.bankAccountTypeId || company?.bankAccountType?.id,
      ) ||
      hasChanged(companyData.bankBranchCode, company?.bankBranchCode) ||
      hasChanged(companyData.bankAccountNumber, company?.bankAccountNumber) ||
      companyData.depotPriceList !== undefined ||
      companyData.freeDeliveryOrderTotal !== undefined;

    const traderFieldSubmissions: ISubmitTraderFormFieldInput[] = [];
    const missingTraderFieldMappings: string[] = [];

    traderFieldChanges.forEach((change) => {
      const field = lshFindTraderFieldForCompanyKey(
        traderApplication,
        change.key,
      );
      if (!field) {
        missingTraderFieldMappings.push(change.label);
        return;
      }

      const submission = lshBuildTraderFormSubmission(field, change.value);
      if (!submission) {
        missingTraderFieldMappings.push(change.label);
        return;
      }

      traderFieldSubmissions.push(submission);
    });

    if (missingTraderFieldMappings.length > 0 && !supportedFieldsChanged) {
      alert(
        `Unable to save: ${missingTraderFieldMappings.join(
          ', ',
        )}. Missing trading form field mapping.`,
      );
      return;
    }

    if (missingTraderFieldMappings.length > 0 && supportedFieldsChanged) {
      alert(
        `Saving supported fields only. Missing trading form mapping for: ${missingTraderFieldMappings.join(
          ', ',
        )}.`,
      );
    }

    if (!supportedFieldsChanged && traderFieldSubmissions.length === 0) {
      alert('No changes detected to save.');
      return;
    }

    setIsLoading(true);

    const requests: Promise<unknown>[] = [];

    if (supportedFieldsChanged) {
      requests.push(
        apiUpdateCompany({
          companyId,
          mainSupplierId: props.user.mainSupplier?.id || '',
          brandSupplierId: props.user.brand?.id || '',
          name: companyData.name || company?.name,
          addressLine1: companyData.addressLine1 || company?.addressLine1,
          addressLine2: companyData.addressLine2 || company?.addressLine2,
          city: companyData.city || company?.city,
          province: companyData.province || company?.province,
          areaCode: companyData.areaCode || company?.areaCode,
          bankId: bankId || undefined,
          bankAccountTypeId: bankAccountTypeId || undefined,
          bankBranchCode: companyData.bankBranchCode || company?.bankBranchCode,
          bankAccountNumber:
            companyData.bankAccountNumber || company?.bankAccountNumber,
          depotPriceList: companyData.depotPriceList ?? company?.depotPriceList,
          freeDeliveryOrderTotal:
            companyData.freeDeliveryOrderTotal ??
            company?.freeDeliveryOrderTotal,
        }),
      );
    }

    if (traderFieldSubmissions.length > 0) {
      if (!traderApplication?.id) {
        setIsLoading(false);
        alert('Missing trader application ID for company form-field save.');
        return;
      }

      requests.push(
        apiSubmitTraderFormFields({
          applicationId: traderApplication.id,
          fields: traderFieldSubmissions,
          companyEntityType: true,
        }),
      );
    }

    Promise.all(requests)
      .then((responses) => {
        const traderResponse = responses.find(
          (response) =>
            Boolean(response) &&
            typeof (response as {success?: unknown}).success === 'boolean',
        ) as {success?: boolean; message?: string} | undefined;

        if (traderResponse && traderResponse.success === false) {
          throw new Error(
            traderResponse.message || 'Failed to update company form fields.',
          );
        }

        setHasSuccessfullyUpdated(true);
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        const errorMessage = Array.isArray(err)
          ? err.join(', ')
          : err?.message || 'Failed to update company at the moment.';
        alert(errorMessage);
        console.error('Failed to update company', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    reduxToggleSidePanel(false, null);
  };

  // Close via side-effect after success
  useEffect(() => {
    if (hasSuccessfullyUpdated) {
      // Give the journey refresh effect time to observe `saved` before unmount.
      const timer = window.setTimeout(() => {
        reduxToggleSidePanel(false, null);
      }, 100);

      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [hasSuccessfullyUpdated]);

  if (hasSuccessfullyUpdated) return null;

  return (
    <FdCompanyForm
      company={company}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle={'Cancel'}
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdCompany;
